import filter from 'lodash/filter';
import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import getOptions from '@gcc/configurator/dist/api/optionsApi';
import filterSelections from '@gcc/configurator/dist/utils/filterSelections';
import {
  beginAjaxCall,
  beginAjaxLoad,
  configuratorLoad,
  configuratorLoadFailure,
  configuratorLoadSuccess,
  endAjaxLoad,
} from '@gcc/configurator/dist/actions/ajaxStatusActions';
import { loadDictionaryTerms } from '@gcc/configurator/dist/actions/dictionaryActions';
import getVisualizerImageUrl from '@gcc/configurator/dist/actions/getVisualizerImageUrl';
import { executeImmediateRules } from '@gcc/configurator/dist/actions/rulesActions';
import { loadOptionsSuccess } from '@gcc/configurator/dist/actions/optionActions';
import { getSettings, setSettings } from '@gcc/configurator/dist/settings';
import { normalizeOptionData } from '@gcc/configurator/dist/modules/mapping';
import { getConfig } from '../../config';

/**
 * gets selected choices with selected store and skus
 * @param {*} choices
 * @param {*} options
 * @param {*} storeNumber
 * @param {*} sku
 */
export function getSelections(choices, options, storeNumber, sku) {
  const isTagExists = (tags, key) => tags && tags.includes(key);

  return filter(choices, (choice) => filterSelections(choice, options, choices))
    .map((choice) => {
      const option = options[choice.optionId];
      let choiceValue = choice.value;
      if (storeNumber && isTagExists(option.tags, 'store-number')) {
        choiceValue = storeNumber;
      }
      if (sku && isTagExists(option.tags, 'surface-sku')) {
        choiceValue = sku;
      }
      return {
        choiceId: choice.id,
        optionId: choice.optionId,
        choiceValue,
        tags: choice.tags,
        optionTags: option.tags,
      };
    });
}
const getPreconfiguration = (preconfiguredChoices) => {
  const preconfig = {};

  preconfiguredChoices.forEach((choice) => {
    preconfig[choice.optionId] = choice;
  });

  return omitBy(preconfig, isUndefined);
};

/**
 * calls getoptions for store change or surface thickness change and retains existing selections
 * @param {*} storeNumber
 * @param {*} sku
 * @param {*} onChoicesLoaded - callback to execute after choices are loaded
 */
export function loadConfiguratorOptions(storeNumber, sku, onChoicesLoaded = null) {
  return (dispatch, getState) => {
    const {
      productId,
      configurationId,
      onLoadSuccess,
      onLoadFailure,
      externalProductId,
      partnerProductId,
    } = getSettings();

    dispatch(configuratorLoad());
    dispatch(beginAjaxLoad('Loading Options'));
    dispatch(beginAjaxCall());

    const {
      configurator: {
        options,
        choices,
      },
      countertops: {
        availableSkus,
      },
    } = getState();

    // eslint-disable-next-line max-len
    const getOptionsApi = `${getConfig()?.customBaseUrl}/countertops/api/configuratoroptions/${storeNumber}/${sku}?availableSkus=${availableSkus}`;
    // update settings with updated options api url
    setSettings({ ...getSettings(), getOptionsApi });

    const existingSelections = getSelections(choices, options, storeNumber, sku);

    return getOptions(getOptionsApi, productId, configurationId, externalProductId, partnerProductId)
      .then(({ data }) => {
        const normalized = normalizeOptionData(data.groups);
        const preselectedChoices = getPreconfiguration(existingSelections);

        // any product data other than groups/options/choices
        // (brand, productType, siteProductName, mainImageUrl, productId, isVisualized)
        const productInfo = omit(data, 'groups');
        // make sure previous dimessions from drafting will be ratained
        // Ensure we get the correct DraftingTool due to timing
        const {
          draftingTool
        } = getState();
        if (!productInfo.additionalDetails) {
          productInfo.additionalDetails = JSON.stringify(draftingTool);
        }
        // getUnavailableChoices needs this to complete first
        const round1 = () => Promise.all([
          dispatch(loadOptionsSuccess(normalized, preselectedChoices, productInfo)),
        ]).then(() => {
          // Execute callback after choices are loaded into Redux state
          if (onChoicesLoaded) {
            onChoicesLoaded();
          }
        });

        // run everything else in "parallel"
        const round2 = () => Promise.all([
          dispatch(executeImmediateRules()),
          dispatch(loadDictionaryTerms()),
        ]);
        const round3 = () => Promise.all([
          dispatch(getVisualizerImageUrl()),
        ]);
        return round1().then(round2).then(round3);
      })
      .catch((err) => {
        console.log(err); // eslint-disable-line no-console
        // TODO Edwin: verify its a bool
        if (onLoadFailure) {
          onLoadFailure();
        }
        dispatch(configuratorLoadFailure());
      })
      .then((results) => {
        dispatch(endAjaxLoad());

        dispatch(configuratorLoadSuccess());
        // TODO Edwin: verify its a bool
        if (onLoadSuccess) {
          onLoadSuccess();
        }
        return results;
      });
  };
}
